import {
    AutoAwesome,
    PlayArrow,
    SmartToy,
    TrendingUp,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    LinearProgress,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { blogService } from "../../../api/services/blogService";

const BlogGenerator = () => {
    const queryClient = useQueryClient();
    const [generating, setGenerating] = useState(false);

    const { data: statusData, isLoading: statusLoading } = useQuery(
        "blog-generator-status",
        () => blogService.getGeneratorStatus(),
        { refetchInterval: generating ? 5000 : 30000 }
    );

    const generateMutation = useMutation(() => blogService.triggerGeneration(), {
        onMutate: () => setGenerating(true),
        onSuccess: (res) => {
            const result = res.data;
            if (result.success) {
                toast.success(`Blog published: "${result.title}"`);
            } else {
                toast.warning(result.error || "Generation skipped");
            }
            queryClient.invalidateQueries("blog-generator-status");
            queryClient.invalidateQueries("blogs");
            setGenerating(false);
        },
        onError: (err: any) => {
            toast.error(
                err?.response?.data?.message || "Failed to generate blog post"
            );
            setGenerating(false);
        },
    });

    const status = statusData?.data;
    const progress = status
        ? Math.round((status.totalGenerated / status.totalTopicsInPool) * 100)
        : 0;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                <SmartToy sx={{ fontSize: 36, color: "primary.main" }} />
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        AI Blog Generator
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Automatically generate SEO-optimized blog posts with Gemini AI
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Generate Card */}
                <Grid item xs={12} md={6}>
                    <Card
                        sx={{
                            height: "100%",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                <AutoAwesome />
                                <Typography variant="h6" fontWeight={600}>
                                    Generate New Post
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 3, color: "rgba(255,255,255,0.85)" }}>
                                Manually trigger AI blog generation. The system picks an unused
                                topic, generates SEO-optimized content with FAQ, and publishes
                                it automatically.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={
                                    generating ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        <PlayArrow />
                                    )
                                }
                                onClick={() => generateMutation.mutate()}
                                disabled={generating}
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.2)",
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                                    backdropFilter: "blur(10px)",
                                }}
                            >
                                {generating ? "Generating..." : "Generate Now"}
                            </Button>
                            {generating && (
                                <Alert
                                    severity="info"
                                    sx={{ mt: 2, bgcolor: "rgba(255,255,255,0.15)", color: "white" }}
                                    icon={false}
                                >
                                    Generating with Gemini AI... This may take 30-60 seconds.
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Status Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                                <TrendingUp color="primary" />
                                <Typography variant="h6" fontWeight={600}>
                                    Generation Status
                                </Typography>
                            </Box>

                            {statusLoading ? (
                                <CircularProgress />
                            ) : status ? (
                                <>
                                    {/* Progress Bar */}
                                    <Box sx={{ mb: 3 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                mb: 1,
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                Topics Used
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                                {status.totalGenerated} / {status.totalTopicsInPool}
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progress}
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Box>

                                    {/* Stats */}
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    bgcolor: "success.50",
                                                    border: "1px solid",
                                                    borderColor: "success.200",
                                                }}
                                            >
                                                <Typography
                                                    variant="h4"
                                                    fontWeight={700}
                                                    color="success.main"
                                                >
                                                    {status.totalGenerated}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Posts Generated
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    bgcolor: "info.50",
                                                    border: "1px solid",
                                                    borderColor: "info.200",
                                                }}
                                            >
                                                <Typography
                                                    variant="h4"
                                                    fontWeight={700}
                                                    color="info.main"
                                                >
                                                    {status.totalTopicsInPool - status.totalGenerated}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Topics Remaining
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Next Topic Preview */}
                                    {status.nextTopic && (
                                        <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                fontWeight={600}
                                            >
                                                NEXT TOPIC
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {status.nextTopic}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Status Chip */}
                                    <Box sx={{ mt: 2 }}>
                                        <Chip
                                            label={status.remaining}
                                            color={
                                                status.remaining === "All topics exhausted"
                                                    ? "warning"
                                                    : "success"
                                            }
                                            size="small"
                                        />
                                    </Box>
                                </>
                            ) : (
                                <Typography color="text.secondary">
                                    Unable to fetch status
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Info Card */}
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                                How it works
                            </Typography>
                            <Grid container spacing={2}>
                                {[
                                    {
                                        title: "Automated Schedule",
                                        desc: "The system runs every 6 hours with a ~40% publish probability, creating 3-5 posts per week for optimal SEO freshness.",
                                    },
                                    {
                                        title: "SEO Optimized",
                                        desc: "Content includes meta tags, FAQ schema (JSON-LD), semantic HTML headings, internal links, and keyword-rich content.",
                                    },
                                    {
                                        title: "Search Indexing",
                                        desc: "After publishing, Google is pinged via sitemap, and IndexNow notifies Bing & Yandex for rapid indexing.",
                                    },
                                    {
                                        title: "Bilingual Content",
                                        desc: "Topics include both English and Arabic content to serve the MENA region audience effectively.",
                                    },
                                ].map((item, i) => (
                                    <Grid item xs={12} sm={6} md={3} key={i}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                bgcolor: "grey.50",
                                                height: "100%",
                                            }}
                                        >
                                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                                {item.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.desc}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default BlogGenerator;
